import React from 'react';
import { Colors, IconButton, Text } from 'library';
import { styled } from '@mui/material';
import { format12HourTime } from 'shared/Dates';
import { Create } from '@mui/icons-material';

import type { TimesheetWorker, UnclockedInTimesheetWorker } from 'parent-portal/home/new/types';
import { CenterMissingPayment } from 'models';

export function needsReview(timesheet: TimesheetWorker | UnclockedInTimesheetWorker | CenterMissingPayment) {
    if (!('hour_report' in timesheet)) {
        return (
            <Pill backgroundColor={Colors.mediumRed}>
                <Text variant="caption" color={Colors.white}>
                    Requires Approval
                </Text>
            </Pill>
        );
    }

    if (!timesheet.hour_report || !timesheet.hour_report.start) return null;

    if (timesheet.hour_report.business_approved)
        return (
            <Pill backgroundColor={Colors.turquoise}>
                <Text variant="caption" color={Colors.white}>
                    Approved
                </Text>
            </Pill>
        );

    if (timesheet.hour_report.end && !timesheet.hour_report.paid)
        return (
            <Pill backgroundColor={Colors.terraCotta}>
                <Text variant="caption" color={Colors.white}>
                    Needs Review
                </Text>
            </Pill>
        );
}

export function hoursReported(timesheet: TimesheetWorker | UnclockedInTimesheetWorker | CenterMissingPayment) {
    if (!('hour_report' in timesheet)) {
        return {
            middle: (
                <Text variant="body2" bold>
                    {format12HourTime(timesheet.start_date, timesheet.timezone)} -{' '}
                    {format12HourTime(timesheet.end_date, timesheet.timezone)}
                </Text>
            ),
            bottom: timesheet.break_minutes ? (
                <Text variant="body2">{timesheet.break_minutes} minute break</Text>
            ) : (
                <Text variant="body2">No break taken</Text>
            ),
        };
    }
    if (!timesheet.hour_report || !timesheet.hour_report.start)
        return {
            middle: (
                <Text variant="body2" bold>
                    Not clocked in
                </Text>
            ),
            bottom: null,
        };

    if (timesheet.hour_report.end) {
        return {
            middle: (
                <Text variant="body2" bold>
                    {format12HourTime(timesheet.hour_report.start, timesheet.timezone)} -{' '}
                    {format12HourTime(timesheet.hour_report.end, timesheet.timezone)}
                </Text>
            ),
            bottom: timesheet.hour_report.approved_break ? (
                <Text variant="body2">{timesheet.hour_report.approved_break} minute break</Text>
            ) : (
                <Text variant="body2">No break taken</Text>
            ),
        };
    }

    const clockedIn = {
        middle: (
            <Text variant="body2">
                <b>Clock In:</b> {format12HourTime(timesheet.hour_report.start, timesheet.timezone)}
            </Text>
        ),
        bottom: null,
    };

    if (timesheet.hour_report.approved_break) {
        return {
            ...clockedIn,
            bottom: <Text variant="body2">{timesheet.hour_report.approved_break} minute break</Text>,
        };
    }

    return clockedIn;
}

export function actions(
    timesheet: TimesheetWorker | UnclockedInTimesheetWorker | CenterMissingPayment,
    editAction: (timesheet: TimesheetWorker | UnclockedInTimesheetWorker | CenterMissingPayment) => void,
    small?: boolean,
) {
    if ('hour_report' in timesheet && timesheet?.hour_report?.paid) {
        return (
            <Pill>
                <Text variant="body2" color={Colors.white}>
                    Paid
                </Text>
            </Pill>
        );
    }

    if ('hour_report' in timesheet && timesheet?.hour_report?.business_approved) {
        return (
            <RowContainer>
                <Container alignItems={small ? 'flex-end' : 'flex-start'}>
                    {small && (
                        <Pill backgroundColor={Colors.turquoise} marginBottom={5}>
                            <Text variant="body2" color={Colors.white}>
                                Approved
                            </Text>
                        </Pill>
                    )}
                    <Pill>
                        <Text variant="body2" color={Colors.white}>
                            Pending
                        </Text>
                    </Pill>
                </Container>
                <IconButton
                    icon={Create}
                    backgroundColor={Colors.darkNavy}
                    color={Colors.white}
                    onClick={() => editAction(timesheet)}
                    style={{ marginLeft: 5 }}
                />
            </RowContainer>
        );
    }

    if (!('hour_report' in timesheet)) {
        return (
            <Container alignItems={small ? 'flex-end' : 'flex-start'}>
                {small && (
                    <Pill backgroundColor={Colors.mediumRed}>
                        <Text variant="body2" color={Colors.white}>
                            Requires Approval
                        </Text>
                    </Pill>
                )}
                <IconButton
                    icon={Create}
                    backgroundColor={Colors.darkNavy}
                    color={Colors.white}
                    onClick={() => editAction(timesheet)}
                    style={{ marginLeft: 5 }}
                />
            </Container>
        );
    }

    return (
        <Container alignItems={small ? 'flex-end' : 'flex-start'}>
            {small && timesheet?.hour_report?.end && (
                <Pill backgroundColor={Colors.terraCotta} marginBottom={5}>
                    <Text variant="body2" color={Colors.white}>
                        Needs Review
                    </Text>
                </Pill>
            )}
            <IconButton
                icon={Create}
                backgroundColor={Colors.darkNavy}
                color={Colors.white}
                onClick={() => editAction(timesheet)}
            />
        </Container>
    );
}
const RowContainer = styled('div')({
    display: 'flex',
    flexDirection: 'row',
});

const Container = styled('div')(({ alignItems }: { alignItems: string }) => ({
    alignItems,
    display: 'flex',
    flexDirection: 'column',
}));

const Pill = styled('div')(
    ({ backgroundColor, marginBottom }: { backgroundColor?: string; marginBottom?: number }) => ({
        backgroundColor: backgroundColor ?? Colors.darkNavy,
        borderRadius: 50,
        marginBottom,
        padding: '5px 10px',
        width: 'fit-content',
    }),
);
